/** App shell: providers, skip link, persistent chrome, route switch.
 *  Route changes restore focus to the page heading and announce the page. */

import { useLayoutEffect, useRef } from "react";

import { AnnouncerProvider, useAnnounce } from "./announcer";
import { Footer, Header } from "./chrome";
import { RouterProvider, useRouter, type RoutePath } from "./router";
import { StudioProvider } from "./store";
import { CollectionPage } from "../pages/Collection";
import { Home } from "../pages/Home";
import { MethodPage } from "../pages/Method";
import { ProjectOpenPage } from "../pages/ProjectOpen";
import { Studio } from "../pages/Studio";

const PAGE_NAMES: Record<RoutePath, string> = {
  "/": "Type Set, home",
  "/collection": "Type Set font shelf",
  "/method": "How Type Set works",
  "/studio": "Your Type Set studio",
  "/open": "Open a saved Type Set project",
};

const Routes = () => {
  const { path } = useRouter();
  const announce = useAnnounce();
  const mainRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    const heading = mainRef.current?.querySelector<HTMLElement>("h1");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    announce(PAGE_NAMES[path]);
  }, [path, announce]);

  return (
    <main id="main" ref={mainRef} tabIndex={-1} className="site-main">
      {path === "/" ? <Home /> : null}
      {path === "/collection" ? <CollectionPage /> : null}
      {path === "/method" ? <MethodPage /> : null}
      {path === "/studio" ? <Studio /> : null}
      {path === "/open" ? <ProjectOpenPage /> : null}
    </main>
  );
};

const Shell = () => {
  return (
    <RouterProvider>
      <a className="skip-link" href="#main">
        Skip to Type Set
      </a>
      <Header />
      <Routes />
      <Footer />
    </RouterProvider>
  );
};

export const App = () => (
  <AnnouncerProvider>
    <StudioProvider>
      <Shell />
    </StudioProvider>
  </AnnouncerProvider>
);
